import { useTrackPage } from '@utilitywarehouse/tracking-react';
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { paths } from '@constants/paths';
import { useSyncPath } from '@features/Partner/useSyncPath';
import { CaptureFocus } from '@features/Remote/Interaction/CaptureFocus';
import { SessionControls } from '@features/Remote/SessionControls';
import { useClearSubmittedBasketContext } from '@hooks/useClearSubmittedBasket';
import { useLoader } from '@hooks/useLoader';
import { useScrollTopOnRouteChange } from '@hooks/useScrollToTop';
import { LocalSessionAddressProvider } from '@providers/basket';
import { useTrackingContext } from '@tracking/Provider/Context';
import { thankYouDetailsStorage } from '@utils/storagePropertyManagement';

const ORDER_PATHS_WITH_DETAILS = [
  paths.confirm,
  paths.deposit,
  paths.thankYou,
] as const;

// Ensures that the tracking path consistency is maintained in FullStory console
// Route with backslash at the end is still valid
const getTrackingPath = (pathname: string) =>
  pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

const usePageTracking = () => {
  const { pathname } = useLocation();
  const { trackingContexts } = useTrackingContext();
  const trackPage = useTrackPage();

  useEffect(() => {
    trackPage(getTrackingPath(pathname), undefined, {
      contexts: trackingContexts,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};

export const useUpdateLoaderProvider = () => {
  const { pathname } = useLocation();
  const { setCurrentPathname } = useLoader();
  useEffect(() => {
    setCurrentPathname(pathname);
  }, [pathname, setCurrentPathname]);
};

const useClearThankYouDetails = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!ORDER_PATHS_WITH_DETAILS.some((p) => pathname.startsWith(p))) {
      thankYouDetailsStorage.remove();
    }
  }, [pathname]);
};

const RouteProvider = () => {
  const syncPath = useSyncPath();
  const { pathname } = useLocation();
  useScrollTopOnRouteChange();
  usePageTracking();
  useUpdateLoaderProvider();
  useClearSubmittedBasketContext();
  useClearThankYouDetails();

  useEffect(() => {
    syncPath(pathname);
  }, [pathname, syncPath]);

  return (
    <LocalSessionAddressProvider>
      <SessionControls>
        <CaptureFocus />
        <Outlet />
      </SessionControls>
    </LocalSessionAddressProvider>
  );
};

export { RouteProvider };
